// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * What has been changed on this machine, and when (design doc §7.6, §9.5, US-C-05, US-C-06).
 *
 * Read-only. Recording a change is an action on the machine, so it lives next to the
 * archive number in the header; this tab is the record of those actions, which is a
 * different thing to look at and mixing the two put a half-filled form in front of anyone
 * who only came to read.
 *
 * `snapshot_unchanged` is shown here rather than explained away: a like-for-like swap
 * produces a version whose assembly is byte-identical to its predecessor (US-C-04), and
 * without the badge the row looks like a no-op somebody filed by mistake.
 *
 * **Clicking a record expands it in place** to show what the event actually did, line by
 * line — which part, which action, how many, and what the position held before and after.
 * The list alone says *that* something changed and why; the detail is the *what*, and it is
 * the thing a person reads this page for when a part fails again a year later.
 *
 * In place rather than in a dialog: this is a history, and the reason to open one entry is
 * usually to compare it with the entry next to it. A dialog would cover the list it was
 * opened from; an expanded row keeps its neighbours on screen, and several can be open at
 * once. The detail is fetched when a row is first opened and then kept, so closing and
 * reopening is instant — the record is immutable (§7.6), so there is nothing to go stale.
 * **全部展开** opens every loaded record at once, for reading a machine's history through.
 *
 * Numbered as one sequence (#1…#N, by the version each brought about), whether a record is
 * the machine's own or one inside a fitted module; a module's record also says which
 * module and its number there, which is what its own page calls it.
 */

import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { api, exportUrls } from "@/api/endpoints";
import { ChangeSummary } from "@/components/ChangeType";
import { CopyButton } from "@/components/CopyButton";
import { ErrorBanner } from "@/components/ErrorBanner";
import { JiraLink } from "@/components/JiraLink";
import { MaintenanceCorrection } from "@/components/MaintenanceCorrection";
import { EventDetail } from "@/components/MaintenanceEventDetail";
import { Pager } from "@/components/Pager";
import { ResizableTable } from "@/components/ResizableTable";
import { useCursorList } from "@/hooks/useCursorList";
import { maintenanceEventLink } from "@/links";
import type { ArchiveContext } from "@/pages/ArchiveDetailPage";

/** The summary row, the expanded panel, and nothing between them. */
const COLUMN_COUNT = 5;

export function ArchiveMaintenance() {
  const { t } = useTranslation();
  const { archiveId = "" } = useParams();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  // Absent when rendered outside the archive page (its tests): then the server decides.
  const currentVersion = useOutletContext<ArchiveContext | undefined>()?.archive.current_version;

  const events = useCursorList(["maintenance", archiveId], (cursor) =>
    api.maintenance.list(archiveId, { cursor, limit: 50 }),
  );

  const allOpen = events.items.length > 0 && events.items.every((e) => open.has(e.event_id));

  const toggle = (eventId: string) =>
    setOpen((previous) => {
      const next = new Set(previous);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });

  return (
    <>
      <div className="filters">
        <a className="link-button" href={exportUrls.maintenance(archiveId)}>
          {t("common.export")}
        </a>
        <button
          type="button"
          disabled={events.items.length === 0}
          onClick={() =>
            setOpen(allOpen ? new Set() : new Set(events.items.map((e) => e.event_id)))
          }
        >
          {allOpen ? t("maintenance.collapseAll") : t("maintenance.expandAll")}
        </button>
      </div>

      <ErrorBanner error={events.error} />

      <ResizableTable storageKey="maintenance-events" className="event-table">
        <thead>
          <tr>
            <th>{t("maintenance.sequence")}</th>
            <th>{t("maintenance.occurredAt")}</th>
            <th>{t("archive.version")}</th>
            <th>{t("maintenance.changeColumn")}</th>
            <th className="col-link">{t("maintenance.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {events.items.map((event) => {
            const expanded = open.has(event.event_id);
            // An event inside a fitted module: recorded on the module, listed here because
            // it gave this archive a version. Its page, link and details are the module's.
            const owner = event.archive_id ?? archiveId;
            const fromModule = Boolean(event.archive_id);
            return (
              <Fragment key={event.event_id}>
                <tr
                  className={expanded ? "event-row is-open" : "event-row"}
                  onClick={() => toggle(event.event_id)}
                >
                  <td>
                    {/* A real button carries the row's click, so the record opens from the
                        keyboard (Enter/Space) as well as by pointer, and aria-expanded tells
                        a screen reader what the click did. */}
                    <button
                      type="button"
                      className="row-activate"
                      aria-expanded={expanded}
                      aria-controls={`event-${event.event_id}`}
                    >
                      <span className="disclosure" aria-hidden="true">
                        {expanded ? "▾" : "▸"}
                      </span>
                      #{event.sequence_no}
                    </button>
                    {fromModule && (
                      <span className="event-module" onClick={(click) => click.stopPropagation()}>
                        {event.module_type && (
                          <span className="module-badge">{event.module_type}</span>
                        )}{" "}
                        <Link to={`/archives/${owner}/maintenance`}>{event.archive_no}</Link>
                        {event.source_sequence_no != null && ` #${event.source_sequence_no}`}
                      </span>
                    )}
                  </td>
                  <td>{new Date(event.occurred_at).toLocaleString()}</td>
                  <td>
                    {fromModule && event.caused_version
                      ? `v${event.caused_version - 1} → v${event.caused_version}`
                      : `v${event.base_version} → v${event.result_version}`}{" "}
                    {event.snapshot_unchanged && (
                      <span className="badge badge-unchanged">
                        {t("maintenance.unchangedBadge")}
                      </span>
                    )}
                  </td>
                  <td>
                    <ChangeSummary changeType={event.change_type} reason={event.reason} />{" "}
                    <JiraLink url={event.jira_url} />
                  </td>
                  {/* Its own page, a link to it for pasting elsewhere, and — within its
                      window — correcting it. None of these opens or closes the row. */}
                  <td className="col-link" onClick={(click) => click.stopPropagation()}>
                    <Link
                      to={
                        fromModule
                          ? `/archives/${owner}/maintenance/${event.event_id}`
                          : event.event_id
                      }
                      aria-label={t("maintenance.openRecord", { n: event.sequence_no })}
                    >
                      {t("maintenance.open")}
                    </Link>
                    <CopyButton
                      value={maintenanceEventLink(owner, event.event_id)}
                      label={t("maintenance.copyLink")}
                    />
                    {/* Edited or revoked on the module's own record, where its window is
                        judged against the module's versions. */}
                    {!fromModule && (
                      <MaintenanceCorrection
                        archiveId={archiveId}
                        event={event}
                        currentVersion={currentVersion}
                      />
                    )}
                  </td>
                </tr>
                {expanded && (
                  <tr className="event-detail-row" id={`event-${event.event_id}`}>
                    <td colSpan={COLUMN_COUNT}>
                      <EventDetail archiveId={owner} event={event} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </ResizableTable>

      {events.items.length === 0 && !events.isLoading && (
        <p className="muted">{t("maintenance.none")}</p>
      )}

      <Pager
        hasNextPage={events.hasNextPage}
        isFetchingNextPage={events.isFetchingNextPage}
        onMore={() => void events.fetchNextPage()}
      />
    </>
  );
}
